/**
 * StickyBottomCTA - Mobile Sticky Action Bar
 * 
 * Redesigned for dual-CTA when both phone & menu are available:
 * - Split layout: Menu (primary green) + Call (secondary dark)
 * - Single action if only one is available
 * - Safe area inset handling
 * - Premium button styling consistent with MenuPreviewCTA
 */

import React from 'react';
import { Phone, BookOpen } from 'lucide-react';

interface StickyBottomCTAProps {
  hasPhone: boolean;
  hasMenu: boolean;
  onCall: () => void;
  onViewMenu: () => void;
}

export const StickyBottomCTA: React.FC<StickyBottomCTAProps> = ({
  hasPhone,
  hasMenu,
  onCall,
  onViewMenu,
}) => {
  if (!hasPhone && !hasMenu) return null;

  const showBoth = hasPhone && hasMenu;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 lg:hidden"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'linear-gradient(to top, rgba(245,246,245,1) 0%, rgba(245,246,245,0.97) 70%, rgba(245,246,245,0) 100%)',
      }}
    >
      <div className={`px-4 py-3 ${showBoth ? 'flex gap-2.5' : ''}`}>
        {/* Menu CTA — primary when both exist */}
        {hasMenu && (
          <button
            onClick={onViewMenu}
            className={`py-3.5 px-5 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${showBoth ? 'flex-1' : 'w-full py-4'}`}
            style={{
              background: 'linear-gradient(170deg, #1ba85e 0%, #0E7F4F 40%, #0a6b3f 100%)',
              boxShadow: '0 4px 16px rgba(14,127,79,0.3), 0 1px 0 rgba(255,255,255,0.12) inset',
            }}
          >
            <BookOpen className="h-[18px] w-[18px] text-white" strokeWidth={2.2} />
            <span className="text-white font-bold text-[14px]">
              {showBoth ? 'Speisekarte' : 'Speisekarte ansehen'}
            </span>
          </button>
        )}
        
        {/* Call CTA — secondary dark when menu also exists */}
        {hasPhone && (
          <button
            onClick={onCall}
            className={`py-3.5 px-5 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${showBoth ? 'flex-[0.65]' : 'w-full py-4'}`}
            style={{
              background: showBoth 
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(170deg, #1ba85e 0%, #0E7F4F 40%, #0a6b3f 100%)',
              boxShadow: showBoth
                ? '0 4px 12px rgba(0,0,0,0.15)'
                : '0 4px 16px rgba(14,127,79,0.3), 0 1px 0 rgba(255,255,255,0.12) inset',
            }}
          >
            <Phone className="h-[18px] w-[18px] text-white" strokeWidth={2.2} />
            <span className="text-white font-bold text-[14px]">
              {showBoth ? 'Anrufen' : 'Jetzt anrufen'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StickyBottomCTA;
